import React, {useEffect, useState, useRef, useCallback} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pageContext } from 'ctm-util-functions';
import { Text, Flexbox, Anchor, Icon } from 'ctm-components';
import { formHelpersSelector, verticalLinkContentSelector, quoteFormSelector } from 'ctm-selectors';
import { useBrandContentFactory } from 'ctm-app/client/hooks/useBrandContentFactory';
import { getExperimentID } from 'ctm-app/client/store/selectors';
import { RedbookDisclosureModal } from './redbook_disclosure_dialog';

const DisclaimerContent = () => {
    const disclaimerContentElement = useRef(null);
    /**
     * When a content from the branding content has an element with attribute [data-id=expandable],
     * it will add the expandable styles to that element and provide the show more/less button
     * Updating this will ensure the button shows appropriately as the reference will only be set
     * after the rerender
     */
    const modalEventListeners = useRef([]);
    const [expandableContent, setExpandableContent] = useState(false);
    const dispatch = useDispatch();
    const isDisclaimerContentExpanded = useSelector((state) => formHelpersSelector(state).get('isDisclaimerContentExpanded'));
    const howComparisonWorksUrl = useSelector((state) => verticalLinkContentSelector(state).get('footer_disclaimer_how_comparison_works_url'));
    const fsgDocumentUrl = useSelector((state) => verticalLinkContentSelector(state).get('footer_disclaimer_financial_services_guide_url'));
    const termsOfUseUrl = useSelector((state) => verticalLinkContentSelector(state).get('footer_disclaimer_terms_of_use_url'));
    const brandContentFactory = useBrandContentFactory();
    const pageState = pageContext.getProps().pageState || {};
    const isCarResultPage = ['results'].includes(pageState.pageName) && pageContext.getVertical() === 'car-insurance';
    const experimentId = useSelector((state) => getExperimentID(state));

    const coverType = useSelector((state) => {
        const form = quoteFormSelector(state);
        return form && form.getIn(['coverDetails', 'coverType']);
    });

    const serverConfig = useSelector((state) => state.get('serverConfig'));

    const hasGnafId = useSelector((state) => {
        const form = quoteFormSelector(state);
        return form && form.getIn(['property', 'address', 'gnafId']);
    });

    const disclaimerContent = brandContentFactory.footerDisclaimer({
        comparisonUrl: howComparisonWorksUrl,
        fsgUrl: fsgDocumentUrl,
        termsOfUseUrl,
        experimentId,
        coverType,
        hasGnafId,
        serverConfig
    });

    /**
     * using the disclaimer content as this unique (to a brand) string will be easier for equality checks
     */
    useEffect(() => {
        setExpandableContent(disclaimerContentElement.current?.querySelector('[data-id=expandable]') || false);
    }, [disclaimerContent]);

    const isPageNameVerifyOrConfirmationOrQuotesExpired = ['verify', 'confirmation', 'quotes_expired'].includes(pageState.pageName);
    const customVariant = isPageNameVerifyOrConfirmationOrQuotesExpired ? 'underline_anchors_verify' : 'underline_anchors';
    const shouldExpandContent = isCarResultPage || isDisclaimerContentExpanded;

    if (expandableContent) {
        if (shouldExpandContent) {
            expandableContent.style.removeProperty('opacity');
            expandableContent.style.removeProperty('height');
            expandableContent.style.removeProperty('overflow');
            expandableContent.style.removeProperty('margin');
        } else {
            expandableContent.style.opacity = 0;
            expandableContent.style.height = 0;
            expandableContent.style.overflow = 'hidden';
            expandableContent.style.margin = 0;
        }
    }

    /**
     * Toggle the disclaimer content actions
     * @param {Boolean} isExpanded - the boolean value to check if the content is expanded/collapsed
     */
    const toggleDisclaimerContent = (isExpanded) => {
        dispatch({
            type: 'SET_VALUE',
            identifier: 'helpers.isDisclaimerContentExpanded',
            value: isExpanded,
            stateKey: 'vertical.quote'
        });
    };

    const handleRedbookDisclosureDialogClose = () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.redbookDisclosure',
            value: { open: false }
        });
    };

    const showRedbookDisclosureDialog = () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.redbookDisclosure',
            value: { open: true }
        });
    };

    /**
     * Get the show more/less toggle links
     * @returns {Node} - the Flexbox containing the show more/less anchors and icons
     */
    const getToggleLinks = () => {
        const iconVariant = isDisclaimerContentExpanded ? 'ctm_angle_down' : 'ctm_angle_up';
        const toggleText = isDisclaimerContentExpanded ? 'Show less' : 'Show more';

        return (
            <Flexbox display="flex">
                <Anchor size="small" href="javascript:;" onClick={() => toggleDisclaimerContent(!isDisclaimerContentExpanded)}>
                    <Icon color="inherit" margin="right" variant={iconVariant} />
                    {toggleText}
                </Anchor>
            </Flexbox>
        );
    };

    const redbookDisclosureHandlers = useCallback((node) => {
        const redbookDisclosureHandler = (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            showRedbookDisclosureDialog();
        };

        if (node !== null) {
            const redbookDisclosureLink = node.querySelector('[data-id=redbookDisclosureOpen]');
            if (redbookDisclosureLink) {
                node.querySelector('[data-id=redbookDisclosureOpen]').addEventListener('click', redbookDisclosureHandler);
                modalEventListeners.current.push(() => { node.querySelector('[data-id=redbookDisclosureOpen]').removeEventListener('click', redbookDisclosureHandler); });
            }
        }

        if (node == null && modalEventListeners.current.length > 0) {
            modalEventListeners.current.forEach((unsubscribe) => unsubscribe());
            modalEventListeners.current = [];
        }
    },
    [isCarResultPage]);

    return (
        <div ref={redbookDisclosureHandlers}>
            <Flexbox display="flex" flex={1} column variant={isPageNameVerifyOrConfirmationOrQuotesExpired ? 'verify_content' : ''}>
                <Text
                    ref={disclaimerContentElement}
                    variant={customVariant}
                    size="small"
                    innerHTML={disclaimerContent} />
                {expandableContent && !isCarResultPage && getToggleLinks()}
            </Flexbox>
            <RedbookDisclosureModal closeModal={handleRedbookDisclosureDialogClose} />
        </div>
    );
};

export { DisclaimerContent };
